<script setup>
import { computed } from 'vue';
import {isGeneratedMatch} from '~/utils/matchActions';
import {useRoute} from 'vue-router';
import {useAuthService} from '@/composables/useAuthService';
import {useValidation} from '@/composables/useValidation';

const props = defineProps({
  fixture: {
    type: Object,
    default: () => ({})
  },
  closed: {
    type: Boolean,
    default: false
  },
  clubId: null,
  leagueCycleDivisionId: {
    type: String,
    required: false
  },
  tournamentStageId: {
    type: String,
    required: false
  },
  rowTeam: {
    type: Object,
    default: () => ({})
  },
  columnTeam: {
    type: Object,
    default: () => ({})
  },
  currentUserTeamIds: {
    type: Array,
    default: () => []
  },
  // the division/stage scoring system — the slim matrix fixtures don't carry it
  scoringSystem: {
    type: Object,
    default: null
  }
});

const route = useRoute();
const { isAdminOfClub, isAuthenticated, user } = useAuthService();
const { isUserInTeam, fixtureResultAction, fixtureResultActionLabel, fixtureScheduleState, fixtureScheduleActionLabel } = useValidation();
const { t } = useI18n();

// --- Fixture state ---
const fixtureExists = computed(() => !!props.fixture?.id);

const isApproved = computed(() => {
  return props.fixture ? props.fixture.requiresApprovalFrom == null : true;
});

// --- User role ---
const isAdmin = computed(() => isAuthenticated.value && isAdminOfClub(props.clubId));

const isMyFixture = computed(() => {
  if (!user.value?.id) return false;
  return isUserInTeam(user.value.id, props.rowTeam) || isUserInTeam(user.value.id, props.columnTeam);
});

// Map the row/column teams to the fixture's actual home/away teams
// props.rowTeam = row team (left side of matrix)
// props.columnTeam = column team (top of matrix)
// props.fixture.homeTeamId/awayTeamId = actual fixture home/away from backend
const fixtureHomeTeam = computed(() => {
  if (!fixtureExists.value) return null;
  if (props.rowTeam.id === props.fixture.homeTeamId) return props.rowTeam;
  if (props.columnTeam.id === props.fixture.homeTeamId) return props.columnTeam;
  return null;
});

const fixtureAwayTeam = computed(() => {
  if (!fixtureExists.value) return null;
  if (props.rowTeam.id === props.fixture.awayTeamId) return props.rowTeam;
  if (props.columnTeam.id === props.fixture.awayTeamId) return props.columnTeam;
  return null;
});

// Determine which team in the fixture the logged-in user is on
const isUserInHomeTeam = computed(() => {
  if (!user.value?.id || !fixtureHomeTeam.value) return false;
  return isUserInTeam(user.value.id, fixtureHomeTeam.value);
});

const isUserInAwayTeam = computed(() => {
  if (!user.value?.id || !fixtureAwayTeam.value) return false;
  return isUserInTeam(user.value.id, fixtureAwayTeam.value);
});

// --- Approval state ---
const needsMyApproval = computed(() => {
  return props.fixture &&
      props.fixture.requiresApprovalFrom != null &&
      props.currentUserTeamIds.includes(props.fixture.requiresApprovalFrom);
});

const myFixture = computed(() => {
  if (!props.fixture) return false;
  return props.currentUserTeamIds.includes(props.fixture.teamId) || props.currentUserTeamIds.includes(props.fixture.opponentTeamId);
});

// --- Permissions ---
// adminEditable/playerEditable may not be in the summary; default to true if absent so the
// dialog can still open and show its own noEditReason if needed.
const canEdit = computed(() => {
  if (!fixtureExists.value) return false;
  if (isAdmin.value) return props.fixture.adminEditable !== false;
  if (isMyFixture.value) return props.fixture.playerEditable !== false;
  return false;
});

// Scheduling uses the same permissions as editing, but not once a result has been entered
// (use the robust result check, not `score` which is null on corrupt fixtures — otherwise
// we'd offer a reschedule the backend now rejects).
const canSchedule = computed(() => canEdit.value && !hasResult.value);

const canDelete = computed(() => {
  if (!fixtureExists.value) return false;
  // Only allow delete once a result exists (robust check — score can be null on corrupt rows).
  // Otherwise use "Cancel Schedule" for scheduled fixtures.
  if (!hasResult.value) return false;

  // Same permission pattern as canEdit
  if (isAdmin.value) return props.fixture.adminEditable !== false;
  if (isMyFixture.value) return props.fixture.playerEditable !== false;
  return false;
});

// For empty cells: admin always, players only when division is not closed
const canCreate = computed(() => {
  if (fixtureExists.value) return false;
  return isAdmin.value || (!props.closed && isMyFixture.value);
});

const nonEditReason = computed(() => {
  if (!fixtureExists.value) return null;
  if (isAdmin.value) return props.fixture.adminNonEditableReason || null;
  if (isMyFixture.value) return props.fixture.playerNonEditableReason || null;
  return null;
});

// Show the dropdown for any admin or participant — the individual items inside are gated.
// Public users (neither admin nor in fixture) get a plain view link instead.
const hasEditActions = computed(() => fixtureExists.value && (isAdmin.value || isMyFixture.value));

// --- Cell styling (presentation only) ---
// Win/draw/loss is decided by POINTS — the row team's points vs the opponent's
// — not by parsing the score string (which can be textual, e.g. "Win - Loss"
// or "Walkover - Forfeit"). `fixture.points` is the row team's points for this
// fixture; the opponent's points come from the column team's own fixture
// against this row.
const opponentPoints = computed(() => {
  const oppFixture = props.columnTeam?.fixtures?.find(f => f.opponentTeamId === props.rowTeam?.id);
  return oppFixture?.points ?? null;
});

// won = success tint pill / drawn = grey ink / lost = default light bg
const scorePillClasses = computed(() => {
  const classes = ['sc'];
  const mine = props.fixture?.points;
  const theirs = opponentPoints.value;
  if (isApproved.value && mine != null && theirs != null) {
    if (mine > theirs) classes.push('sc--won');
    else if (mine === theirs) classes.push('sc--drawn');
  }
  return classes.join(' ');
});

// pending approval = small warning dot; brand dot when it's waiting on me / the admin
const showPendingDot = computed(() => fixtureExists.value && !isApproved.value);
const pendingDotClasses = computed(() =>
    'pend' + ((needsMyApproval.value || (isAdmin.value && !myFixture.value)) ? ' pend--mine' : ''));

// --- Schedule state ---
const hasConfirmedSchedule = computed(() => {
  return fixtureExists.value && props.fixture.scheduledAt != null;
});

const hasProposedSchedule = computed(() => {
  return fixtureExists.value &&
         (props.fixture.homeTeamScheduledAt != null || props.fixture.awayTeamScheduledAt != null);
});

// Determine which team in the fixture the current row team (teamId) represents
// fixture.teamId = row team (current perspective)
// fixture.homeTeamId & awayTeamId = actual home/away in the fixture entity
const isRowTeamHomeInFixture = computed(() => {
  if (!fixtureExists.value) return false;
  return props.fixture.teamId === props.fixture.homeTeamId;
});

// These work for anyone viewing (including admins not in the fixture)
// Determine if the row team has proposed a schedule
const rowTeamProposedSchedule = computed(() => {
  if (!hasProposedSchedule.value) return false;

  const rowTeamSchedule = isRowTeamHomeInFixture.value
    ? props.fixture.homeTeamScheduledAt
    : props.fixture.awayTeamScheduledAt;
  const opponentSchedule = isRowTeamHomeInFixture.value
    ? props.fixture.awayTeamScheduledAt
    : props.fixture.homeTeamScheduledAt;

  return rowTeamSchedule != null && (opponentSchedule == null || rowTeamSchedule === opponentSchedule);
});

// Determine if the opponent team (column team) has proposed a schedule
const opponentTeamProposedSchedule = computed(() => {
  if (!hasProposedSchedule.value) return false;

  const rowTeamSchedule = isRowTeamHomeInFixture.value
    ? props.fixture.homeTeamScheduledAt
    : props.fixture.awayTeamScheduledAt;
  const opponentSchedule = isRowTeamHomeInFixture.value
    ? props.fixture.awayTeamScheduledAt
    : props.fixture.homeTeamScheduledAt;

  return opponentSchedule != null && rowTeamSchedule == null;
});

// These only work for players in the fixture
// Check based on the logged-in user's team (not the row team)
const proposedByMe = computed(() => {
  if (!isMyFixture.value) return false;

  const mySchedule = isUserInHomeTeam.value
    ? props.fixture.homeTeamScheduledAt
    : props.fixture.awayTeamScheduledAt;
  const opponentSchedule = isUserInHomeTeam.value
    ? props.fixture.awayTeamScheduledAt
    : props.fixture.homeTeamScheduledAt;

  return mySchedule != null && (opponentSchedule == null || mySchedule === opponentSchedule);
});

const proposedByOpponent = computed(() => {
  if (!isMyFixture.value) return false;

  const mySchedule = isUserInHomeTeam.value
    ? props.fixture.homeTeamScheduledAt
    : props.fixture.awayTeamScheduledAt;
  const opponentSchedule = isUserInHomeTeam.value
    ? props.fixture.awayTeamScheduledAt
    : props.fixture.homeTeamScheduledAt;

  return opponentSchedule != null && mySchedule == null;
});

const scheduleDateToShow = computed(() => {
  if (hasConfirmedSchedule.value) return props.fixture.scheduledAt;

  // For players in the fixture, show based on user's team (not row team)
  if (proposedByOpponent.value) {
    return isUserInHomeTeam.value
      ? props.fixture.awayTeamScheduledAt
      : props.fixture.homeTeamScheduledAt;
  }
  if (proposedByMe.value) {
    return isUserInHomeTeam.value
      ? props.fixture.homeTeamScheduledAt
      : props.fixture.awayTeamScheduledAt;
  }

  // Fallback for admins/non-participants: show based on row team
  if (hasProposedSchedule.value) {
    if (rowTeamProposedSchedule.value) {
      return isRowTeamHomeInFixture.value
        ? props.fixture.homeTeamScheduledAt
        : props.fixture.awayTeamScheduledAt;
    }
    if (opponentTeamProposedSchedule.value) {
      return isRowTeamHomeInFixture.value
        ? props.fixture.awayTeamScheduledAt
        : props.fixture.homeTeamScheduledAt;
    }
    return props.fixture.homeTeamScheduledAt || props.fixture.awayTeamScheduledAt;
  }
  return null;
});

const formattedScheduleDate = computed(() => {
  if (!scheduleDateToShow.value) return null;
  const { $luxon } = useNuxtApp();
  const date = $luxon.fromISO(scheduleDateToShow.value);
  return {
    date: date.toFormat('dd/MMM'),
    time: date.toFormat('HH:mm')
  };
});

const scheduleLabel = computed(() => {
  // Participants get the shared state machine's wording (useValidation —
  // mirrored by the mobile app). Admins/non-participants keep the generic
  // labels: their dialog is approve/change, not confirm-vs-counter.
  if (isMyFixture.value) {
    const myTeamId = isUserInHomeTeam.value ? props.fixture.homeTeamId : props.fixture.awayTeamId;
    return fixtureScheduleActionLabel(fixtureScheduleState(props.fixture, myTeamId), 'Schedule');
  }
  if (hasConfirmedSchedule.value) return 'Reschedule';
  return 'Schedule';
});

const scheduleTooltip = computed(() => {
  if (hasConfirmedSchedule.value) return 'Confirmed schedule';
  if (proposedByOpponent.value) return 'Opponent has proposed a schedule. Click to confirm or suggest a different date';
  if (proposedByMe.value) return 'You have proposed a schedule. Waiting for opponent to confirm';

  // For admins not in the fixture, show which player proposed
  if (hasProposedSchedule.value && isAdmin.value) {
    // Determine which team proposed
    const homeProposed = props.fixture.homeTeamScheduledAt != null;
    const awayProposed = props.fixture.awayTeamScheduledAt != null;

    if (homeProposed && !awayProposed && fixtureHomeTeam.value) {
      return `${fixtureHomeTeam.value.name} proposed a schedule. Click to approve or change`;
    }
    if (awayProposed && !homeProposed && fixtureAwayTeam.value) {
      return `${fixtureAwayTeam.value.name} proposed a schedule. Click to approve or change`;
    }
    // Both proposed (same date) or unclear
    return 'Proposed schedule. Click to approve or change';
  }

  return 'No schedule yet. Click to schedule';
});

const publicScheduleTooltip = computed(() => {
  if (hasConfirmedSchedule.value) return 'Confirmed schedule. Click to view match';
  if (hasProposedSchedule.value) return 'Proposed schedule. Click to view match';
  return t('view_fixtures', 1);
});

// Record result / Approve result / Edit result — via the shared helper. The matrix
// payload carries requiresApprovalFrom (a teamId), handled by the helper's matrix branch.
const editResultLabel = computed(() => {
  if (!fixtureExists.value) return t('edit_result');
  return fixtureResultActionLabel(
      fixtureResultAction(props.fixture, {isAdmin: isAdmin.value, teamIds: props.currentUserTeamIds}));
});

const hasSchedule = computed(() => {
  return hasConfirmedSchedule.value || hasProposedSchedule.value;
});

// Robust "has a result" for the matrix payload: score/playedAt can both be null on corrupt
// fixtures, but points are set once a result is approved — treat any of them as a result.
const hasResult = computed(() =>
    fixtureExists.value && (props.fixture.score != null || props.fixture.playedAt != null || props.fixture.points != null));

const tdStyles = "min-width: 72px; max-width: 110px; width: 72px;";

// --- Live scoring ---
// gate on the scoring system (fixture's own if the payload has it, else the
// division/stage system passed down) and on being a participant/admin
const supportsLive = computed(() =>
    props.fixture?.supportsLiveScoring
    ?? props.fixture?.scoringSystem?.supportsLiveScoring
    ?? props.scoringSystem?.supportsLiveScoring
    ?? false);

const canLiveScore = computed(() =>
    supportsLive.value && !hasResult.value && (isAdmin.value || isMyFixture.value));

// empty cell: the fixture doesn't exist yet — create it resultless, then open the counter
// (same pattern as ladder challenges; createItem's response lands in the crud item ref)
const {
  createItem: createLiveFixture,
  item: liveFixtureItem,
  hasFormErrors: hasLiveStartErrors,
} = useCrudApi('api/v2/fixtures');

const startLiveScoring = async () => {
  if (props.fixture?.id) return navigateTo(`/fixtures/${props.fixture.id}/live`);
  await createLiveFixture({
    leagueCycleDivisionId: props.leagueCycleDivisionId ?? undefined,
    tournamentStageId: props.tournamentStageId ?? undefined,
    scoringSystemId: props.scoringSystem?.id ?? props.fixture?.scoringSystem?.id,
    homeTeamId: props.rowTeam.id,
    awayTeamId: props.columnTeam.id,
    approve: false,
  });
  if (hasLiveStartErrors.value) return;
  const id = liveFixtureItem.value?.id;
  if (id) await navigateTo(`/fixtures/${id}/live`);
};
</script>

<template>
  <!-- Self-match diagonal -->
  <td :style="tdStyles" v-if="rowTeam.id === columnTeam.id">
    <div class="text-center">
      <span class="self">✕</span>
    </div>
  </td>

  <!-- Fixture exists with a score result -->
  <td :style="tdStyles" class="py-2" v-else-if="fixtureExists && fixture.score">
    <div class="d-flex justify-center">
      <!-- View only: direct link -->
      <v-tooltip v-if="!hasEditActions" :text="t('view_fixtures', 1)" location="top">
        <template #activator="{ props: tooltipProps }">
          <router-link v-bind="tooltipProps" :to="`/fixtures/${fixture.id}`" class="text-decoration-none">
            <span class="sc-wrap cursor">
              <span :class="scorePillClasses">
                <span class="pts" v-if="isApproved && fixture.points != null">{{ fixture.points }}</span>
                <span>{{ fixture.score }}</span>
              </span>
              <span v-if="showPendingDot" :class="pendingDotClasses"></span>
            </span>
          </router-link>
        </template>
      </v-tooltip>

      <!-- Has actions: v-menu dropdown -->
      <v-menu v-else location="bottom" :close-on-content-click="true">
        <template #activator="{ props: menuProps }">
          <v-tooltip text="Show actions" location="top">
            <template #activator="{ props: tooltipProps }">
              <span v-bind="{...menuProps, ...tooltipProps}" class="sc-wrap cursor">
                <span :class="scorePillClasses">
                  <span class="pts" v-if="isApproved && fixture.points != null">{{ fixture.points }}</span>
                  <span>{{ fixture.score }}</span>
                </span>
                <span v-if="showPendingDot" :class="pendingDotClasses"></span>
              </span>
            </template>
          </v-tooltip>
        </template>
        <v-list class="pa-0" min-width="170">
          <v-list-item :exact="true" prepend-icon="mdi-eye" :prepend-gap="12" :title="t('view_fixtures', 1)"
                       :to="`/fixtures/${fixture.id}`"/>
          <v-list-item :exact="true" v-if="canEdit" prepend-icon="mdi-pencil" :prepend-gap="12" :title="editResultLabel"
                       :to="{...route, query: {...route.query, editingFixtureId: fixture.id}}"/>
          <v-list-item :exact="true" v-if="canSchedule" prepend-icon="mdi-calendar" :prepend-gap="12" :title="scheduleLabel"
                       :to="{...route, query: {...route.query, schedulingFixtureId: fixture.id}}"/>
          <!-- Clear result: admin-only, generated fixtures only (keeps the draw/rotation-generated fixture) -->
          <v-list-item :exact="true" v-if="isAdmin && hasResult && (fixture.tournamentStageId || fixture.americanoCycleDivisionId)" prepend-icon="mdi-eraser" :prepend-gap="12" title="Clear result"
                       class="text-error font-weight-bold"
                       :to="{...route, query: {...route.query, clearingResultFixtureId: fixture.id}}"/>
          <!-- League fixtures: Delete (removes fixture) — never generated ones -->
          <v-list-item :exact="true" v-if="canDelete && !fixture.tournamentStageId && !fixture.americanoCycleDivisionId" prepend-icon="mdi-delete" :prepend-gap="12" :title="t('delete_fixture')"
                       class="text-error font-weight-bold"
                       :to="{...route, query: {...route.query, deletingFixtureId: fixture.id}}"/>
          <v-list-item v-if="nonEditReason" prepend-icon="mdi-information" :title="nonEditReason" :disabled="true"/>
        </v-list>
      </v-menu>
    </div>
  </td>

  <!-- Fixture exists with schedule/result — show to users with edit access -->
  <td :style="tdStyles" class="py-2 text-center" v-else-if="fixtureExists && hasEditActions && (hasSchedule || hasResult)">
    <v-menu location="bottom" :close-on-content-click="true">
      <template #activator="{ props: menuProps }">
        <v-tooltip :text="scheduleTooltip" location="top">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="{...menuProps, ...tooltipProps}"
                  class="dt cursor"
                  :class="hasConfirmedSchedule ? '' : 'dt--tbc'">
              <template v-if="formattedScheduleDate">
                {{ formattedScheduleDate.date }}<br>{{ formattedScheduleDate.time }}
              </template>
              <template v-else>TBC</template>
            </span>
          </template>
        </v-tooltip>
      </template>
      <v-list class="pa-0" density="compact" min-width="170">
        <v-list-item :exact="true" v-if="fixture.score || fixture.playedAt" prepend-icon="mdi-eye" :prepend-gap="12" :title="t('view_fixtures', 1)"
                     :to="`/fixtures/${fixture.id}`"/>
        <v-list-item :exact="true" v-if="canEdit" prepend-icon="mdi-pencil" :prepend-gap="12" :title="editResultLabel"
                     :to="{...route, query: {...route.query, editingFixtureId: fixture.id}}"/>
        <v-list-item :exact="true" v-if="canSchedule" prepend-icon="mdi-calendar" :prepend-gap="12" :title="scheduleLabel"
                     :to="{...route, query: {...route.query, schedulingFixtureId: fixture.id}}"/>
        <v-list-item :exact="true" v-if="canLiveScore" prepend-icon="mdi-scoreboard-outline" :prepend-gap="12" title="Live score"
                     :to="`/fixtures/${fixture.id}/live`"/>
        <!-- Generated matches (tournament draws + americano rotations):
             Clear time keeps the match; league/ladder matches are DELETED, so
             the two actions are named for what they do (audit A4). -->
        <v-list-item :exact="true" v-if="hasSchedule && !fixture.score && (canEdit || isAdmin) && isGeneratedMatch(fixture)" prepend-icon="mdi-calendar-remove" :prepend-gap="12" title="Clear time"
                     class="text-warning"
                     :to="{...route, query: {...route.query, clearingScheduleFixtureId: fixture.id}}"/>
        <v-list-item :exact="true" v-else-if="hasSchedule && !fixture.score && (canEdit || isAdmin)" prepend-icon="mdi-delete" :prepend-gap="12" title="Delete match"
                     class="text-error"
                     :to="{...route, query: {...route.query, cancelingScheduleFixtureId: fixture.id}}"/>
        <!-- Clear result: admin-only, generated fixtures only (keeps them) -->
        <v-list-item :exact="true" v-if="isAdmin && hasResult && (fixture.tournamentStageId || fixture.americanoCycleDivisionId)" prepend-icon="mdi-eraser" :prepend-gap="12" title="Clear result"
                     class="text-error"
                     :to="{...route, query: {...route.query, clearingResultFixtureId: fixture.id}}"/>
        <!-- League fixtures: Delete (removes fixture) — never generated ones -->
        <v-list-item :exact="true" v-if="canDelete && !fixture.tournamentStageId && !fixture.americanoCycleDivisionId" prepend-icon="mdi-delete" :prepend-gap="12" :title="t('delete_fixture')"
                     class="text-error"
                     :to="{...route, query: {...route.query, deletingFixtureId: fixture.id}}"/>
        <v-list-item v-if="nonEditReason" prepend-icon="mdi-information" :title="nonEditReason" :disabled="true"/>
      </v-list>
    </v-menu>
  </td>

  <!-- Fixture exists with schedule/result — public view (no edit access) -->
  <td :style="tdStyles" class="py-2 text-center" v-else-if="fixtureExists && !hasEditActions && (hasSchedule || hasResult)">
    <v-tooltip :text="publicScheduleTooltip" location="top">
      <template #activator="{ props: tooltipProps }">
        <router-link v-bind="tooltipProps" :to="`/fixtures/${fixture.id}`" class="text-decoration-none">
          <span v-if="formattedScheduleDate" class="dt cursor" :class="hasConfirmedSchedule ? '' : 'dt--tbc'">
            {{ formattedScheduleDate.date }}<br>{{ formattedScheduleDate.time }}
          </span>
          <span v-else class="dt dt--tbc cursor">TBC</span>
        </router-link>
      </template>
    </v-tooltip>
  </td>

  <!-- Fixture exists but is empty (no schedule, no result) — show grey chip for edit access -->
  <td :style="tdStyles" class="text-center" v-else-if="fixtureExists && hasEditActions">
    <!-- Closed for this player's match -->
    <span v-if="closed && !isAdmin && isMyFixture" class="tl-chip tl-chip--warn">Closed</span>

    <!-- Actions available: dropdown with editingFixtureId -->
    <v-menu v-else location="bottom" :close-on-content-click="true">
      <template #activator="{ props: menuProps }">
        <v-tooltip text="Show actions" location="top">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="{...menuProps, ...tooltipProps}" class="add cursor">+</span>
          </template>
        </v-tooltip>
      </template>
      <v-list class="pa-0" min-width="170">
        <v-list-item :exact="true" prepend-icon="mdi-pencil" title="Record result" :prepend-gap="12"
                     :to="{...route, query: {...route.query, editingFixtureId: fixture.id}}"/>
        <v-list-item :exact="true" prepend-icon="mdi-calendar" title="Schedule" :prepend-gap="12"
                     :to="{...route, query: {...route.query, schedulingFixtureId: fixture.id}}"/>
        <v-list-item :exact="true" v-if="canLiveScore" prepend-icon="mdi-scoreboard-outline" title="Live score" :prepend-gap="12"
                     :to="`/fixtures/${fixture.id}/live`"/>
      </v-list>
    </v-menu>
  </td>

  <!-- No fixture yet -->
  <td :style="tdStyles" class="text-center" v-else-if="!fixtureExists">
    <!-- Closed for this player's match -->
    <span v-if="closed && !isAdmin && isMyFixture" class="tl-chip tl-chip--warn">Closed</span>

    <!-- Actions available: dropdown -->
    <v-menu v-else-if="canCreate" location="bottom" :close-on-content-click="true">
      <template #activator="{ props: menuProps }">
        <v-tooltip text="Show actions" location="top">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="{...menuProps, ...tooltipProps}" class="add cursor">+</span>
          </template>
        </v-tooltip>
      </template>
      <v-list class="pa-0" min-width="170">
        <v-list-item :exact="true" prepend-icon="mdi-pencil" title="Record result" :prepend-gap="12"
                     :to="{...route, query: leagueCycleDivisionId ? {leagueCycleDivisionId: leagueCycleDivisionId, homeTeamId: rowTeam.id, awayTeamId: columnTeam.id} : {tournamentStageId: tournamentStageId, homeTeamId: rowTeam.id, awayTeamId: columnTeam.id}}"/>
        <v-list-item :exact="true" prepend-icon="mdi-calendar" title="Schedule" :prepend-gap="12"
                     :to="{...route, query: leagueCycleDivisionId ? {schedulingLeagueCycleDivisionId: leagueCycleDivisionId, homeTeamId: rowTeam.id, awayTeamId: columnTeam.id} : {schedulingTournamentStageId: tournamentStageId, homeTeamId: rowTeam.id, awayTeamId: columnTeam.id}}"/>
        <v-list-item v-if="supportsLive" prepend-icon="mdi-scoreboard-outline" title="Live score" :prepend-gap="12"
                     @click="startLiveScoring"/>
      </v-list>
    </v-menu>

    <!-- Not played, nothing the viewer can do -->
    <span v-else class="dash">–</span>
  </td>

  <!-- Fallback empty -->
  <td :style="tdStyles" class="text-center" v-else>
    <span class="dash">–</span>
  </td>
</template>

<style scoped lang="scss">
.cursor { cursor: pointer; }

/* Remove default link styling to preserve cell colors */
a {
  color: inherit !important;
  text-decoration: none;
}

/* wrapper so the pending dot can overflow the pill (which is overflow:hidden for
   the score ellipsis) without being clipped */
.sc-wrap {
  position: relative;
  display: inline-block;
}

/* score pill — lost by default (light bg / charcoal ink) */
.sc {
  position: relative;
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 7px;
  border-radius: 7px;
  background: rgb(var(--v-theme-light));
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
  /* Clip long scores (e.g. "Walkover - Forfeit") to a single ellipsised line,
     matching the Flutter matrix _ScoreChip. A FIXED max-width is required: the
     table is auto-layout, so a `<td>` max-width is ignored and the column would
     otherwise expand to fit the nowrap text. 96px comfortably fits a numeric
     "555 - 555" (with the points prefix) and clips the long word results. */
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;

  &.sc--won {
    background: rgb(var(--v-theme-successTint));
    color: rgb(var(--v-theme-success));
  }

  &.sc--drawn {
    background: rgb(var(--v-theme-light));
    color: rgb(var(--v-theme-text));
  }

  /* points earned — quiet count ahead of the score */
  .pts {
    font-size: 9px;
    font-weight: 700;
    opacity: 0.65;
    margin-right: 4px;
    padding-right: 4px;
    border-right: 1px solid currentColor;
  }
}

/* pending approval dot */
.pend {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-warning));
  border: 1.5px solid rgb(var(--v-theme-surface));

  &.pend--mine {
    background: rgb(var(--v-theme-primary));
  }
}

/* schedule chip — bordered; dashed warning when only suggested */
.dt {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 600;
  line-height: 1.25;
  color: rgb(var(--v-theme-charcoal));
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-border));
  border-radius: 7px;
  padding: 3px 6px;
  white-space: nowrap;

  &.dt--tbc {
    border-style: dashed;
    color: rgb(var(--v-theme-warning));
  }
}

/* enter result — dashed brand circle */
.add {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgb(var(--v-theme-surface));
  border: 1.5px dashed rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-primary));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 600;
  line-height: 1;
}

/* not played */
.dash {
  color: #D8D8D8;
  font-size: 13px;
  font-weight: 600;
}

/* self-match */
.self {
  color: #E2E2E2;
  font-size: 13px;
}
</style>
